import React, { useState, useEffect } from "react";
import ChannelCard from "./components/ChannelCard";
import CreateChannelModal from "./components/CreateChannelModal";
import { useAuth } from "../../../../../firebase/AuthContext";

// ── Constants ─────────────────────────────────────────────────────────────────

const DICT = {
  channelsTitle: { en: "Channels", zh: "頻道" },
  channelsDesc: { en: "channels created", zh: "個頻道已建立" },
  createChannel: { en: "Create Channel", zh: "建立頻道" },
  channelName: { en: "Channel Name", zh: "頻道名稱" },
  channelNamePh: { en: "e.g. Partner Portal", zh: "例如：合作夥伴入口" },
  ownerLabel: { en: "Owner (optional)", zh: "擁有者（選填）" },
  ownerPh: { en: "e.g. user@email.com", zh: "例如：user@email.com" },
  cancel: { en: "Cancel", zh: "取消" },
  create: { en: "Create", zh: "建立" },
  regenerateKey: { en: "Regenerate Key", zh: "重新產生金鑰" },
  noChannels: { en: "No channels yet", zh: "尚無頻道" },
  noChannelsDesc: {
    en: "Create your first API channel to get started",
    zh: "建立您的第一個 API 頻道以開始使用",
  },
  copied: { en: "Copied!", zh: "已複製！" },
  copyKey: { en: "Copy", zh: "複製" },
  products: { en: "products", zh: "個產品" },
  pins: { en: "pins", zh: "個綁定" },
  noOwner: { en: "No owner", zh: "無擁有者" },
  manage: { en: "Manage", zh: "管理" },
  loading: { en: "Loading channels…", zh: "載入頻道中…" },
  fetchError: { en: "Failed to load channels.", zh: "載入頻道失敗。" },
  creating: { en: "Creating…", zh: "建立中…" },
  createError: { en: "Failed to create channel.", zh: "建立頻道失敗。" },
};

const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

// ── Channels Tab (main export) ────────────────────────────────────────────────
export default function ChannelsTab({
  language,
  currentUser,
  apiKeys,
  loading,
  fetchError,
  onRetry,
}) {
  const [channels, setChannels] = useState(apiKeys || []);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { hasRole } = useAuth();
  const canCreate = hasRole("api.create");
  const [copiedId, setCopiedId] = useState(null);

  // Sync channels whenever the parent updates apiKeys
  useEffect(() => {
    setChannels(apiKeys || []);
  }, [apiKeys]);

  const handleCopyKey = (channelId, apiKey) => {
    navigator.clipboard.writeText(apiKey);
    setCopiedId(channelId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleChannelDeleted = (channelId) => {
    setChannels((prev) => prev.filter((c) => c.channel_id !== channelId));
  };

  return (
    <div className="animate-slideUp space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {t("channelsTitle", language)}
            </h2>
            <p className="text-gray-500 text-sm">
              {channels.length} {t("channelsDesc", language)}
            </p>
          </div>
          {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 font-medium shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-105"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t("createChannel", language)}
          </button>
          )}
        </div>
      </div>

      {/* Channel Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 text-gray-500">
            <svg
              className="w-5 h-5 animate-spin text-orange-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span className="text-sm">{t("loading", language)}</span>
          </div>
        </div>
      ) : fetchError ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-red-200 shadow-sm gap-2">
          <p className="text-red-500 font-medium">{fetchError}</p>
          <button
            onClick={onRetry}
            className="mt-2 px-4 py-2 text-sm bg-orange-50 text-orange-600 rounded-xl border border-orange-200 hover:bg-orange-100 transition-all"
          >
            {language === "zh" ? "重試" : "Retry"}
          </button>
        </div>
      ) : channels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="w-20 h-20 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-orange-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
          <p className="text-gray-900 text-lg font-medium mb-1">
            {t("noChannels", language)}
          </p>
          <p className="text-gray-500 text-sm">
            {t("noChannelsDesc", language)}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {channels.map((channel) => (
            <ChannelCard
              key={channel.channel_id}
              channel={channel}
              language={language}
              currentUser={currentUser}
              copiedId={copiedId}
              onCopy={handleCopyKey}
              onDeleted={handleChannelDeleted}
            />
          ))}
        </div>
      )}

      {/* Create Channel Modal */}
      {showCreateModal && (
        <CreateChannelModal
          language={language}
          currentUser={currentUser}
          onCreated={onRetry}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}
